import { CommonModule, DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { SoftSlotDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
import { ButtonComponent } from '@softline/ui-core';
import { IconComponent } from '@softline/ui-core';
import { NumberBadgeComponent } from '@softline/ui-core';
import { NumberBadgeVariant } from '@softline/ui-core';
import { ProgressStep, ProgressStepsOrientation } from './progress-steps.types';

let nextProgressStepsId = 0;

@Component({
  selector: 'soft-progress-steps, soft-ds-progress-steps',
  imports: [
    CommonModule,
    RouterModule,
    NgTemplateOutlet,
    ButtonComponent,
    SoftSlotDirective,
    IconComponent,
    NumberBadgeComponent,
    TranslatePipe,
  ],
  templateUrl: './progress-steps.component.html',
  styleUrl: './progress-steps.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape($event)',
  },
})
export class ProgressStepsComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  readonly steps = input<ProgressStep[]>([]);
  /** Index into `steps` of the active step. Clicking a clickable step selects it
      (tab behaviour); bind two-way to follow the user's position. */
  readonly currentIndex = model(0);
  readonly showLabels = input(true);
  readonly orientation = input<ProgressStepsOrientation>('horizontal');

  readonly allowJump = input(false);

  /** Collapsed/expanded state of the mobile presentation. Internal only. */
  protected readonly mobileOpen = signal(false);

  /**
   * Viewport-relative rect for the fixed-position mobile panel (see its CSS
   * comment): computed from the host on open since `position: fixed` can't
   * anchor to a normal-flow ancestor like `inset: 0 0 auto 0` on `absolute` did.
   */
  protected readonly panelRect = signal<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  /** Stable id linking the chevron trigger (aria-controls) to the panel. */
  protected readonly panelId = `soft-ds-progress-steps-panel-${nextProgressStepsId++}`;

  private readonly mobileTrigger = viewChild('mobileTrigger', { read: ElementRef });

  constructor() {
    // Scroll doesn't bubble, so a capturing document listener is the only way
    // to notice scrolling on any ancestor (not just the window) while the
    // viewport-fixed panel is open, and close it before it visually drifts.
    const onScroll = () => {
      if (this.mobileOpen()) this.closeMobile(false);
    };
    this.document.addEventListener('scroll', onScroll, true);
    this.destroyRef.onDestroy(() =>
      this.document.removeEventListener('scroll', onScroll, true)
    );
  }

  protected readonly hostClasses = computed(
    () => `soft-progress-steps-host orientation-${this.orientation()}`
  );

  readonly visibleSteps = computed(() =>
    this.steps().filter((s) => s.state !== 'hidden')
  );

  readonly visibleIndex = computed(() => {
    const steps = this.steps();
    const currentIndex = this.currentIndex();

    if (currentIndex >= steps.length) return this.visibleSteps().length;
    if (currentIndex < 0) return -1;

    return (
      steps.slice(0, currentIndex + 1).filter((step) => step.state !== 'hidden').length -
      1
    );
  });

  readonly visibleCurrent = computed(() => this.visibleSteps()[this.visibleIndex()]);

  readonly currentPosition = computed(() => {
    const total = this.visibleSteps().length;
    return Math.min(Math.max(this.visibleIndex() + 1, 0), total);
  });

  readonly hasAnyContentTemplate = computed(() =>
    this.visibleSteps().some((s) => !!s.contentTemplate)
  );

  isClickable(step: ProgressStep, index: number): boolean {
    if (step.state === 'disabled') return false;
    if (
      !this.allowJump() &&
      index > this.visibleIndex() &&
      !step.done &&
      !this.isReachable(index)
    ) {
      return false;
    }
    return !!step.routerLink || !!step.execute;
  }

  /** A step ahead of the current one opens up once every step before it is done. */
  private isReachable(index: number): boolean {
    const steps = this.visibleSteps();
    for (let i = 0; i < index; i++) {
      if (!this.isDone(steps[i], i)) return false;
    }
    return true;
  }

  isDone(step: ProgressStep, index: number): boolean {
    return step.done ?? this.visibleIndex() > index;
  }

  /**
   * Variant for the numbered/custom-icon number-badge: the active (current,
   * not done) step is filled `primary`; every other numbered step is `outline`.
   * Done/error steps don't use the number-badge at all (see template).
   */
  numberBadgeVariant(index: number): NumberBadgeVariant {
    return this.visibleIndex() === index ? 'primary' : 'outline';
  }

  /** Whether the step contributes any text to the label block. */
  hasLabel(step: ProgressStep): boolean {
    return !!step.header || !!step.subHeader;
  }

  onStepKeydown(event: Event, step: ProgressStep, index: number): void {
    if (!this.isClickable(step, index)) return;

    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement | null)?.click();
  }

  handleClick(event: MouseEvent, step: ProgressStep, index: number): void {
    if (!this.isClickable(step, index)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.currentIndex.set(this.stepIndexOf(index));
    if (step.execute) step.execute();
  }

  /** Maps a visible-track index back to its position in `steps` (hidden steps skipped). */
  private stepIndexOf(visibleIndex: number): number {
    let seen = -1;
    const steps = this.steps();
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].state === 'hidden') continue;
      if (++seen === visibleIndex) return i;
    }
    return visibleIndex;
  }

  // ---- Mobile presentation (disclosure) --------------------------------

  toggleMobile(): void {
    const willOpen = !this.mobileOpen();
    this.mobileOpen.set(willOpen);
    if (willOpen) this.updatePanelRect();
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (this.mobileOpen()) this.updatePanelRect();
  }

  private updatePanelRect(): void {
    const rect = this.host.nativeElement.getBoundingClientRect();
    this.panelRect.set({ top: rect.bottom, left: rect.left, width: rect.width });
  }

  /** Close the panel, optionally returning focus to the chevron trigger. */
  closeMobile(restoreFocus = true): void {
    if (!this.mobileOpen()) return;
    this.mobileOpen.set(false);
    if (restoreFocus) this.focusTrigger();
  }

  /** Selecting a reachable step navigates (router/execute) and closes the panel. */
  handleMobileClick(event: MouseEvent, step: ProgressStep, index: number): void {
    this.handleClick(event, step, index);
    if (this.isClickable(step, index)) this.mobileOpen.set(false);
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.mobileOpen()) return;
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) this.closeMobile(false);
  }

  onEscape(event: Event): void {
    if (!this.mobileOpen()) return;
    event.stopPropagation();
    this.closeMobile();
  }

  private focusTrigger(): void {
    this.mobileTrigger()?.nativeElement.querySelector('button')?.focus();
  }
}
